import React, { useCallback, useEffect, useMemo, useState } from "react";
import { logActivity } from "../../utils/activity";
import { todayStr, yesterdayStr, startOfMonth, RANGE_PRESETS, activePresetKey } from "../../utils/dateRange";
import { buildCsv, downloadCsv, locSlug } from "../../utils/csv";
import { formatDateWithYear as formatDay, formatTime } from "../../utils/format";

// isDay reports whether the from/to range is exactly the single day `d`.
const isDay = (from, to, d) => from === d && to === d;

const GAMES_CLOUD_BASE = (process.env.REACT_APP_GAMES_CLOUD_URL || "")
    .replace(/^ws/, "http")
    .replace(/ws$/, "");

const LIST_LABELS = { open: "Åbne", day: "Dagen", close: "Lukke" };

function api(path, opts = {}) {
    const token = localStorage.getItem("accessToken");
    return fetch(`${GAMES_CLOUD_BASE}${path}`, {
        ...opts,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    });
}

// ChecklistHistory is the "Historik" admin tab: a log of work done or missed — every
// scheduled task occurrence over a period with its done/not-done status (same quick-select
// presets as the scoreboard). Occurrences whose deadline hasn't passed yet are left out by
// the backend, so today's open tasks never read as "not done". An admin can un-tick a
// completed one.
export default function ChecklistHistory({ config }) {
    const locationKeys = useMemo(() => Object.keys(config?.locations || {}), [config]);
    const [location, setLocation] = useState(""); // "" = all locations
    const [status, setStatus] = useState("all"); // all | done | undone
    const [from, setFrom] = useState(startOfMonth());
    const [to, setTo] = useState(todayStr());
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const locationName = key => config?.locations?.[key]?.displayName || key;
    const activePreset = activePresetKey(from, to);
    function applyPreset(p) { setFrom(p.from()); setTo(todayStr()); }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api(`checklist/history?from=${from}&to=${to}&location=${encodeURIComponent(location)}`);
            if (r.ok) setRows((await r.json()).history || []);
            else setRows([]);
        } finally {
            setLoading(false);
        }
    }, [from, to, location]);

    useEffect(() => { load(); }, [load]);

    async function uncomplete(row) {
        if (!window.confirm(`Sæt "${row.title}" (${formatDay(row.day)}) som IKKE udført?`)) return;
        const r = await api("checklist/uncomplete", {
            method: "POST", body: JSON.stringify({ task_id: row.task_id, date: row.day }),
        });
        if (!r.ok) { alert("Kunne ikke opdatere."); return; }
        logActivity("checklist_task_undone", `${row.title} — ${row.day}`);
        load();
    }

    const showLoc = location === "";
    const shown = rows.filter(r => status === "all" || (status === "done" ? r.done : !r.done));
    const cols = showLoc ? 6 : 5;

    function exportCsv() {
        if (!shown.length) { alert("Ingen opgaver at eksportere."); return; }
        const header = ["Dato", "Lokation", "Liste", "Opgave", "Status", "Udført af", "MedarbejderID", "Point"];
        const rowsCsv = shown.map(r => [
            r.day,
            locationName(r.location),
            LIST_LABELS[r.list_type] || r.list_type,
            r.title,
            r.done ? "Udført" : "Ikke udført",
            r.done ? r.done_by_name : "",
            r.done ? r.done_by_employee_id : "",
            r.points,
        ]);
        const loc = locSlug(location ? locationName(location) : "alle-lokationer");
        downloadCsv(`checkliste-historik_${loc}_${from}_${to}.csv`, buildCsv(header, rowsCsv));
        logActivity("checklist_history_export", `${loc} ${from}..${to} (${shown.length} rk.)`);
    }

    return (
        <>
            <div className="acl-scope">
                <label>Lokation
                    <select value={location} onChange={e => setLocation(e.target.value)}>
                        <option value="">Alle lokationer</option>
                        {locationKeys.map(k => <option key={k} value={k}>{locationName(k)}</option>)}
                    </select>
                </label>
                <label>Status
                    <select value={status} onChange={e => setStatus(e.target.value)}>
                        <option value="all">Alle</option>
                        <option value="done">Kun udført</option>
                        <option value="undone">Kun ikke udført</option>
                    </select>
                </label>
                <label>Fra<input type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
                <label>Til<input type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
                <div className="acl-presets">
                    <button className={`acl-preset ${isDay(from, to, todayStr()) ? "active" : ""}`}
                        onClick={() => { setFrom(todayStr()); setTo(todayStr()); }}>I dag</button>
                    <button className={`acl-preset ${isDay(from, to, yesterdayStr()) ? "active" : ""}`}
                        onClick={() => { setFrom(yesterdayStr()); setTo(yesterdayStr()); }}>I går</button>
                    {RANGE_PRESETS.map(p => (
                        <button key={p.key}
                            className={`acl-preset ${activePreset === p.key ? "active" : ""}`}
                            onClick={() => applyPreset(p)}>
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            <section className="acl-card">
                <div className="acl-card-head">
                    <h2>Historik — planlagte opgaver</h2>
                    <button className="acl-preset" onClick={exportCsv}>Download CSV</button>
                </div>
                <table className="acl-table">
                    <thead>
                        <tr>
                            <th>Dato</th>
                            {showLoc && <th>Lokation</th>}
                            <th>Liste</th>
                            <th>Opgave</th>
                            <th>Status / Udført af</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.length === 0 && (
                            <tr><td colSpan={cols} className="acl-dim">
                                {loading ? "Henter…" : "Ingen opgaver i perioden."}
                            </td></tr>
                        )}
                        {shown.map(row => (
                            <tr key={`${row.task_id}-${row.day}`}>
                                <td className="acl-dim" style={{ whiteSpace: "nowrap" }}>
                                    {formatDay(row.day)}{row.done && ` · ${formatTime(row.completed_at)}`}
                                </td>
                                {showLoc && <td>{locationName(row.location)}</td>}
                                <td>{LIST_LABELS[row.list_type] || row.list_type}</td>
                                <td>{row.title}</td>
                                <td>
                                    {row.done
                                        ? <><span className="acl-ok">✓</span> {row.done_by_name}</>
                                        : <span className="acl-undone">✗ Ikke udført</span>}
                                </td>
                                <td className="acl-row-actions">
                                    {row.done && (
                                        <button className="danger" onClick={() => uncomplete(row)}>Sæt som ikke udført</button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        </>
    );
}
